import {
  Alert,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  SvgIcon,
  Typography,
  useTheme,
} from "@mui/material";
import { CategorizationMap } from "portal/utils/categoryCollection/category";
import { classes, STALE_CHIP_COLOR } from "portal/theme/theme";
import {
  DEFAULT_AVERAGE_PPCM,
  Dimensions,
  getPlantAwareCropping,
} from "portal/utils/categoryCollection/image";
import { Image } from "portal/components/images/Image";
import { isNil } from "common/utils/identity";
import { PaddingDerivedPointLabelOverlay } from "../../imageOverlays/PointLabelOverlay";
import { PolaroidOverlay } from "../../imageOverlays/PolaroidOverlay";
import { portalApi } from "portal/state/portalApi";
import { PredictionPoint } from "protos/veselka/prediction_point";
import { RadioChipOption, RadioChips } from "portal/components/RadioChips";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  ThumbnailImage,
  ThumbnailWrapper,
} from "portal/components/images/ThumbnailImage";
import { titleCase } from "common/utils/strings";
import { UploadedByOperatorImageOverlay } from "../../imageOverlays/IconOverlay";
import { useCategoryCollectionCustomerId } from "../../CategoryCollectionCustomerContext";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import CloseIcon from "@mui/icons-material/Close";
import ModelIcon from "portal/images/icons/model.svg?react";
import React, { FC, useMemo } from "react";
import TimeIcon from "@mui/icons-material/AccessTime";

interface PredictionTabProps {
  category: RadioChipOption;
  similarityScore?: number;
  matchingPredictionPointId?: string;
  categorizationMap: CategorizationMap;
  categoryOptions: RadioChipOption[];
  isStale: boolean;
  onReclassify?: (predictionPointId: string, categoryId: string) => void;
}
export const PredictionTab: FC<PredictionTabProps> = ({
  category,
  isStale,
  similarityScore,
  matchingPredictionPointId,
  categorizationMap,
  categoryOptions,
  onReclassify,
}) => {
  const { t } = useTranslation();
  const categoryCollectionCustomerId = useCategoryCollectionCustomerId();

  const {
    currentData: matchingPredictionPointResponse,
    isLoading: isMatchingPredictionPointLoading,
    error: matchingPredictionPointError,
  } = portalApi.endpoints.listPredictionPoints.useQuery(
    matchingPredictionPointId && categoryCollectionCustomerId
      ? {
          page: 1,
          pageSize: 1,
          ids: [matchingPredictionPointId],
          customerIds: [categoryCollectionCustomerId],
        }
      : skipToken
  );
  const matchingPredictionPoint = matchingPredictionPointResponse?.data[0];
  const matchingCategoryOption = matchingPredictionPointId
    ? categoryOptions.find(
        (option) => option.id === categorizationMap[matchingPredictionPointId]
      )
    : undefined;
  return (
    <div className="text-xs md:text-sm pb-2 px-2 lg:pb-4 lg:px-4 flex flex-col gap-2 lg:gap-4 overflow-y-auto">
      {isStale && (
        <Alert
          severity="info"
          className="italic rounded-md text-xs"
          classes={{
            icon: "text-inherit",
          }}
          style={{
            background: STALE_CHIP_COLOR.bg,
            color: STALE_CHIP_COLOR.text,
          }}
          icon={<TimeIcon className="text-lg" />}
        >
          {t("components.categoryCollectionProfile.session.stale")}
        </Alert>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex gap-2 items-center">
          <Typography variant="caption">
            {titleCase(
              t(
                "components.categoryCollectionProfile.session.predictedCategory"
              )
            )}
          </Typography>
          <Chip
            className="font-normal p-1 w-fit"
            style={{
              borderColor: isStale ? STALE_CHIP_COLOR.bg : category.color?.bg,
              color: isStale ? STALE_CHIP_COLOR.text : category.color?.text,
              backgroundColor: isStale
                ? STALE_CHIP_COLOR.bg
                : category.color?.bg,
            }}
            variant="outlined"
            label={category.name}
            size="small"
            icon={
              <SvgIcon
                style={{
                  color: isStale ? STALE_CHIP_COLOR.text : category.color?.text,
                }}
                className="text-sm"
              >
                {isStale ? <TimeIcon /> : <ModelIcon />}
              </SvgIcon>
            }
          />
        </div>
        <div className="flex gap-2 items-center">
          <Typography variant="caption">
            {t("models.crops.fields.confidence.name")}
          </Typography>
          {isNil(similarityScore) ? undefined : (
            <MatchConfidence similarityScore={similarityScore} />
          )}
        </div>
        {matchingPredictionPointId && (
          <Divider className="border-gray-400 my-2" />
        )}
        {matchingPredictionPointId && (
          <div className="flex items-center flex-col gap-2">
            <Typography variant="caption">
              {titleCase(
                t(
                  "components.categoryCollectionProfile.session.categorizedImageMatch"
                )
              )}
            </Typography>
            <div
              style={{
                borderColor: isStale
                  ? STALE_CHIP_COLOR.bg
                  : matchingCategoryOption?.color?.bg,
                color: isStale
                  ? STALE_CHIP_COLOR.text
                  : matchingCategoryOption?.color?.text,
                backgroundColor: isStale
                  ? STALE_CHIP_COLOR.bg
                  : matchingCategoryOption?.color?.bg,
              }}
              className="flex flex-col items-center"
            >
              <ClassificationMatchImage
                predictionPoint={matchingPredictionPoint}
                categorizationMap={categorizationMap}
                categoryOptions={categoryOptions}
                isStale={isStale}
                isLoading={isMatchingPredictionPointLoading}
                error={Boolean(
                  matchingPredictionPointError ||
                    (matchingPredictionPointResponse?.data &&
                      matchingPredictionPointResponse.data.length === 0)
                )}
              />
              {onReclassify && (
                <div
                  className={classes("flex gap-1 pb-2 pl-2", {
                    "pt-2": !matchingCategoryOption,
                  })}
                >
                  <RadioChips
                    isLoading={isMatchingPredictionPointLoading}
                    options={categoryOptions}
                    activeId={categorizationMap[matchingPredictionPointId]}
                    setActiveId={(categoryId) => {
                      if (!matchingPredictionPoint) {
                        return;
                      }
                      onReclassify(matchingPredictionPoint.id, categoryId);
                    }}
                    size="small"
                    forceDropdown
                  />
                  <IconButton
                    className={
                      !matchingPredictionPoint ||
                      !categorizationMap[matchingPredictionPointId]
                        ? "text-gray-400"
                        : "text-white"
                    }
                    disabled={
                      !matchingPredictionPoint ||
                      !categorizationMap[matchingPredictionPointId]
                    }
                    onClick={() => {
                      if (
                        !matchingPredictionPoint ||
                        !categorizationMap[matchingPredictionPointId]
                      ) {
                        return;
                      }
                      onReclassify(
                        matchingPredictionPoint.id,
                        categorizationMap[matchingPredictionPointId]
                      );
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface MatchConfidenceProps {
  similarityScore: number;
}
const MatchConfidence: FC<MatchConfidenceProps> = ({ similarityScore }) => {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const similarities = {
    low: {
      text: t("utils.descriptors.low"),
      threshold: 0.75,
      className: "text-red-300",
      style: { color: muiTheme.palette.error.main },
    },
    medium: {
      text: t("utils.descriptors.medium"),
      threshold: 0.9,
      style: { color: muiTheme.palette.warning.main },
    },
    high: {
      text: t("utils.descriptors.high"),
      threshold: 1,
      style: { color: muiTheme.palette.success.main },
    },
  };
  let similarity = similarities.high;
  if (similarityScore < similarities.low.threshold) {
    similarity = similarities.low;
  } else if (similarityScore < similarities.medium.threshold) {
    similarity = similarities.medium;
  }
  const percent = Math.max(0, Math.min(similarityScore * 100, 100));
  return (
    <Typography variant="caption" style={similarity.style}>{`${
      similarity.text
    } (${percent.toFixed(2)}${t("utils.units.%")})`}</Typography>
  );
};
const MATCHING_IMAGE_DIMENSIONS = { width: 175, height: 175 };
interface ClassificationMatchImageProps {
  dimensions?: Dimensions;
  predictionPoint?: PredictionPoint;
  isLoading?: boolean;
  error?: boolean;
  categoryOptions: RadioChipOption[];
  categorizationMap: CategorizationMap;
  isStale: boolean;
}
const ClassificationMatchImage: FC<ClassificationMatchImageProps> = ({
  dimensions = MATCHING_IMAGE_DIMENSIONS,
  predictionPoint,
  isLoading,
  error,
  categoryOptions,
  categorizationMap,
  isStale,
}) => {
  const userPreferences = useUserPreferences();

  const thumbnail = useMemo(() => {
    if (!predictionPoint?.image) {
      return;
    }
    const {
      x,
      y,
      radius,
      id,
      image: { url, ppcm, uploadedByOperator },
    } = predictionPoint;
    const {
      x: croppedX,
      y: croppedY,
      width: croppedWith,
      height: croppedHeight,
      paddingFactor,
    } = getPlantAwareCropping({
      x,
      y,
      radius,
    });
    const radiusCm = radius / (ppcm || DEFAULT_AVERAGE_PPCM);
    const matchingCategoryId = categorizationMap[id];
    const matchingCategoryOption = categoryOptions.find(
      (option) => option.id === matchingCategoryId
    );
    return {
      id,
      url,
      x: croppedX,
      y: croppedY,
      width: croppedWith,
      height: croppedHeight,
      radius,
      ppcm,
      className: matchingCategoryOption
        ? undefined
        : "grayscale brightness-125",
      renderOverlay: () => (
        <>
          {userPreferences.categoryCollection.showLabels && (
            <PaddingDerivedPointLabelOverlay
              padding={dimensions.width * paddingFactor}
              dimensions={dimensions}
            />
          )}
          {uploadedByOperator && <UploadedByOperatorImageOverlay />}
          {matchingCategoryOption && (
            <PolaroidOverlay
              dimensions={dimensions}
              radiusCm={
                userPreferences.categoryCollection.showLabels
                  ? radiusCm
                  : undefined
              }
              color={isStale ? STALE_CHIP_COLOR : matchingCategoryOption.color}
              position="bottom"
            />
          )}
        </>
      ),
    };
  }, [
    categorizationMap,
    categoryOptions,
    dimensions,
    isStale,
    predictionPoint,
    userPreferences.categoryCollection.showLabels,
  ]);

  if (isLoading) {
    <ThumbnailWrapper dimensions={MATCHING_IMAGE_DIMENSIONS}>
      <Skeleton
        variant="rectangular"
        width={MATCHING_IMAGE_DIMENSIONS.width}
        height={MATCHING_IMAGE_DIMENSIONS.height}
      />
    </ThumbnailWrapper>;
  }

  if (error) {
    return (
      <ThumbnailWrapper dimensions={dimensions}>
        <Image image={{ isSrcError: true }} />
      </ThumbnailWrapper>
    );
  }
  return (
    <ThumbnailWrapper dimensions={dimensions}>
      <ThumbnailImage image={thumbnail} />
    </ThumbnailWrapper>
  );
};
